import { Fragment, useEffect, useState } from 'react';
import { Portal } from '@aviv-estimation-funnel/estimation-data-models/enums';
import { CSSObject, Global } from '@emotion/react';
import { Box, useTokens } from '@gemini/core';
import type { Tokens } from '@gemini/tokens';
import { TextButton } from '@gemini/ui';
import { useLocale, useTranslation } from '@aviv-unified-frontend/runtime/i18n';
import { NAME_SPACE } from '@mfe-presentation/hooks/useLoadTranslations';
import { isFrenchPortal } from '@mfe-helpers/portalHelpers';
import DidomiUserConsent from '@aviv-estimation-funnel/shells/src/app/components/DidomiUserConsent';
import { TAppEnv } from '@mfe-types/env';

import { loadUserCentricsScript } from '../../helpers/headScript';

type TFooterPosition = 'fixed' | 'static';

interface IFooterProps {
    copyright: string;
    links?: Record<string, Record<string, string>>;
    openCookieBanner: () => void;
    portal: Portal;
}

interface ILinkProps {
    name: string;
    href: string;
}

const currentYear = new Date().getFullYear();

const footerStyles = (tokens: Tokens): CSSObject => ({
    '.footer': {
        textWrap: 'nowrap',
        'a, button': {
            color: tokens['color.content.default.default'],
            fontWeight: 'normal',
            '@media (max-width: 400px)': {
                fontSize: '12px',
            },
            '&:hover': {
                color: tokens['color.content.interactive.hover'],
                textDecoration: 'underline',
            },
        },
    },
});

const updateFooterPosition = (
    contentHeight: number,
    callBack: (newPositionValue: TFooterPosition) => void,
) => {
    /* This is used to render the footer below the mfes on the result page only.
    This way, the mfes are never covered by the footer.
    On all other pages, the footer is always visible, without scrolling down.*/
    if (window.location.pathname.split('/').includes('result')) {
        callBack(contentHeight > window.innerHeight ? 'static' : 'fixed');
    } else {
        callBack('fixed');
    }
};

const Footer = ({ copyright, links, openCookieBanner, portal }: IFooterProps) => {
    const { t } = useTranslation(NAME_SPACE);
    const { locale } = useLocale();
    const tokens = useTokens();
    const [footerPosition, setFooterPosition] = useState<TFooterPosition>('fixed');

    useEffect(() => {
        // IWT / IMN have Usercentrics on Tealium
        // TODO: With the harmonization of the tracking, Usercentrics script will be implement on the code for all brands
        if (portal === Portal.ImmoWeb) {
            loadUserCentricsScript(locale);
        }
    }, []);

    const resizeObserver = new ResizeObserver((entries) => {
        const contentHeight = entries[0].contentRect.height;
        updateFooterPosition(contentHeight, setFooterPosition);
    });

    const Cookie = () => (
        <TextButton size="14" onPress={openCookieBanner}>
            {t('common.footer.cookieSettings')}
        </TextButton>
    );

    const Link = ({ name, href }: ILinkProps) => (
        <Fragment key={name}>
            <TextButton size="14" href={href} isExternal>
                {t(`common.footer.${name}`)}
            </TextButton>
        </Fragment>
    );

    const CopyRight = () => (
        <Box typography="typography.body.12.regular">
            {copyright} {currentYear} - {t('common.footer.allRightsReserved')}
        </Box>
    );

    useEffect(() => {
        const rootElement = document.getElementById('root');
        if (!rootElement) return;

        resizeObserver.observe(rootElement);
        return () => resizeObserver.unobserve(rootElement);
    }, []);

    return (
        <Box
            position={footerPosition}
            bottom="0"
            left="0"
            as="footer"
            width="100%"
            backgroundColor="color.background.light"
        >
            {isFrenchPortal(portal) && (
                <DidomiUserConsent
                    portal={portal}
                    env={process.env.NX_TASK_TARGET_CONFIGURATION as TAppEnv}
                />
            )}
            <Global styles={footerStyles(tokens)} />
            <Box
                marginHorizontal="auto"
                alignItems="center"
                display="flex"
                flexDirection="column"
                gap="spacing.4"
                justifyContent="center"
                alignContent="center"
                width="100%"
                height={{
                    'breakpoint.base': '48px',
                    'breakpoint.sm': '56px',
                }}
                paddingHorizontal={{
                    'breakpoint.base': 'spacing.16',
                    'breakpoint.sm': 'spacing.none',
                }}
                unsafe_className="footer"
                borderTopWidth="borderWidth.1"
                borderTopStyle="solid"
                borderTopColor="color.border.light.default"
            >
                <>
                    <Box
                        marginHorizontal="auto"
                        alignItems="center"
                        display="flex"
                        flexDirection="row"
                        gap={{
                            'breakpoint.base': 'spacing.8',
                            'breakpoint.sm': 'spacing.24',
                        }}
                        justifyContent="center"
                        alignContent="center"
                        width="100%"
                        height="sizing.16"
                    >
                        <Cookie />
                        {links &&
                            Object.keys(links).map((name) => (
                                <Link key={name} href={links[name][locale]} name={name} />
                            ))}
                    </Box>
                    <CopyRight />
                </>
            </Box>
        </Box>
    );
};

export default Footer;
